class PrescriptionSwap {
    constructor() {
        this.isProcessing = false;
        this.digitalRecipeIdEl = document.getElementById('prescription-config');
        this.physicalRecipeIdEl = document.getElementById('prescription-config');

        this.additionalProducts = {
            digitalRecipe: this.digitalRecipeIdEl ? parseInt(this.digitalRecipeIdEl.dataset.digitalRecipeId || '0') : 55931859632463,
            physicalRecipe: this.digitalRecipeIdEl ? parseInt(this.digitalRecipeIdEl.dataset.physicalRecipeId || '0') : 55878119260495
        }

        // Kept separate from additionalProducts: checkRecipeStatus treats every ID
        // in there as a recipe and would remove the fee as a "wrong recipe".
        this.serviceFeeId = this.digitalRecipeIdEl ? parseInt(this.digitalRecipeIdEl.dataset.serviceFeeId || '0') : 57901465960783;
    }

    async processRecipeOnly() {
        return this.processOption('digital', this.additionalProducts.digitalRecipe, 'recipe only');
    }

    async processProductWithRecipe() {
        return this.processOption('physical', this.additionalProducts.physicalRecipe, 'product with recipe');
    }

    /**
     * Generic function to process either option
     * @param {string} targetType - 'digital' or 'physical'
     * @param {number} recipeProductId - The recipe product variant ID to add
     * @param {string} optionName - Name for logging purposes
     * @returns {Promise<void>}
     */
    async processOption(targetType, recipeProductId, optionName) {
        if (this.isProcessing) return;

        this.isProcessing = true;
        this.updateButtonStates(true);

        try {
            const cartRes = await fetch('/cart.js');
            const cartData = await cartRes.json();

            if (!cartData.items || cartData.items.length === 0) return;

            await this.normalizeCartToType(cartData.items, targetType);

            await this.ensureCorrectRecipeType(cartData.items, recipeProductId, targetType);

            await this.ensureServiceFee(cartData.items);

            if (cartData.items.length === 1) {
                if (cartData.items[0].properties.__recipe_type) {
                    if (optionName === 'recipe only') {
                        const button = document.querySelector('[data-prescription-action="recipe-only"]');
                        if (!(button instanceof HTMLButtonElement)) return;
                        const redirectUrl = button.dataset.digitalCollectionUrl;
                        if (!redirectUrl) return;
                        window.location.href = redirectUrl;
                    } else if (optionName === 'product with recipe') {
                        const button = document.querySelector('[data-prescription-action="product-with-recipe"]');
                        if (!(button instanceof HTMLButtonElement)) return;
                        const redirectUrl = button.dataset.physicalCollectionUrl;
                        if (!redirectUrl) return;
                        window.location.href = redirectUrl;
                    }
                } else {
                    window.location.href = '/cart';
                }
            } else {
                window.location.href = '/cart';
            }

        } catch (error) {
            console.error(`🔄 Error processing ${optionName}:`, error);
            this.showError(`An error occurred while processing the ${optionName} process`);
        } finally {
            this.isProcessing = false;
            this.updateButtonStates(false);
        }
    }

    /**
     * Normalizes the cart to a given type
     * @param {Array<{id: number, variant_id: number, product_title: string, handle: string, quantity: number, properties: Object}>} cartItems - The cart items to normalize
     * @param {string} targetType - The type to normalize the cart to
     * @returns {Promise<void>}
     */
    async normalizeCartToType(cartItems, targetType) {
        const recipeProductIds = Object.values(this.additionalProducts);

        for (const item of cartItems) {
            if (recipeProductIds.includes(item.variant_id) || item.variant_id === this.serviceFeeId) {
                continue;
            }
            const mapping = /** @type {any} */ (window).prescriptionMapping.findCounterpart(item.id);
            if (mapping && mapping.type !== targetType) {
                await this.swapSingleProduct(item, mapping, targetType);
            } else if (mapping && mapping.type === targetType) {
            } else {
                console.log(`${item.handle} has no mapping`);
            }
        }
    }

    /**
     * Checks if there is an existing recipe product in the cart
     * @param {Array<{variant_id: number, product_title: string}>} cartItems - The cart items to check
     * @returns {boolean} - True if an existing recipe product is found, false otherwise
     */
    checkForExistingRecipeProduct(cartItems) {
        const recipeProductIds = Object.values(this.additionalProducts);

        const existingRecipe = cartItems.find(item =>
            recipeProductIds.includes(item.variant_id)
        );

        if (existingRecipe) {
            return true;
        }

        return false;
    }

    /**
     * Ensures the correct recipe type is in the cart
     * @param {Array<{variant_id: number, product_title: string}>} cartItems - The cart items to check
     * @param {number} correctRecipeId - The correct recipe variant ID
     * @param {string} targetType - The target type to ensure
     * @returns {Promise<void>}
     */
    async ensureCorrectRecipeType(cartItems, correctRecipeId, targetType) {
        const recipeStatus = this.checkRecipeStatus(cartItems, correctRecipeId);

        if (recipeStatus.hasCorrectRecipe) {
            return;
        }

        if (recipeStatus.hasWrongRecipe) {
            await this.removeRecipeProduct(recipeStatus.wrongRecipeItem?.id);
        }

        await this.addRecipeProduct(correctRecipeId);
    }

    /**
     * Checks the recipe status in the cart
     * @param {Array<{variant_id: number, product_title: string}>} cartItems - The cart items to check
     * @param {number} correctRecipeId - The correct recipe variant ID
     * @returns {{hasCorrectRecipe: boolean, hasWrongRecipe: boolean, wrongRecipeItem: {variant_id: number, product_title: string} | null, correctRecipeItem: {variant_id: number, product_title: string} | null}} - Status object with recipe information     */
    checkRecipeStatus(cartItems, correctRecipeId) {
        const recipeProductIds = Object.values(this.additionalProducts);

        const recipeItems = cartItems.filter(item =>
            recipeProductIds.includes(item.variant_id)
        );

        const correctRecipeItem = recipeItems.find(item =>
            item.variant_id === correctRecipeId
        );

        const wrongRecipeItem = recipeItems.find(item =>
            item.variant_id !== correctRecipeId
        );

        return {
            hasCorrectRecipe: !!correctRecipeItem,
            hasWrongRecipe: !!wrongRecipeItem,
            wrongRecipeItem: wrongRecipeItem || null,
            correctRecipeItem: correctRecipeItem || null
        };
    }

    /**
     * Removes a recipe product from the cart
     * @param {number} itemId - The id of the item to remove
     * @returns {Promise<void>}
     */
    async removeRecipeProduct(itemId) {

        const response = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                updates: { [itemId]: 0 }
            })
        });

        if (!response.ok) {
            throw new Error(`Failed to remove recipe product: ${response.statusText}`);
        }
    }

    /**
     * 
     * @param {number} variantId 
     * @returns {Promise<void>}
     */
    async addRecipeProduct(variantId) {

        const recipeType = variantId === this.additionalProducts.digitalRecipe ? 'digital' : 'physical';

        const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id: variantId,
                quantity: 1,
                properties: {
                    __recipe_type: recipeType
                }
            })
        });

        if (!response.ok) {
            throw new Error(`Failed to add recipe product: ${response.statusText}`);
        }

    }

    /**
     * Ensures the service fee product is in the cart (added once, regardless of
     * which delivery option was chosen)
     * @param {Array<{variant_id: number, product_title: string}>} cartItems - The cart items to check
     * @returns {Promise<void>}
     */
    async ensureServiceFee(cartItems) {
        if (!this.serviceFeeId) return;

        const alreadyInCart = cartItems.some(item => item.variant_id === this.serviceFeeId);
        if (alreadyInCart) return;

        const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id: this.serviceFeeId,
                quantity: 1,
                properties: {
                    __service_fee: 'true'
                }
            })
        });

        if (!response.ok) {
            throw new Error(`Failed to add service fee product: ${response.statusText}`);
        }
    }

    /**
     * Swaps a single product to recipe only
     * @param {Object} item - The cart item to swap
     * @param {string} item.handle - The handle of the product
     * @param {number} item.id - The id of the product
     * @param {number} item.quantity - The quantity of the product
     * @param {Object} item.properties - The properties of the product
     * @param {Object} mapping - The mapping for the product
     * @param {number} mapping.alternative - The alternative product id
     * @param {string} targetType - The type to swap the product to
     * @returns {Promise<void>}
     */
    async swapSingleProduct(item, mapping, targetType = 'digital') {

        const updatedProperties = {
            ...item.properties,
            '__product_type': targetType
        }

        const addResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id: mapping.alternative,
                quantity: item.quantity,
                properties: updatedProperties || {}
            })
        });

        if (!addResponse.ok) {
            throw new Error(`Failed to add recipe only version: ${addResponse.statusText}`);
        }

        const removeResponse = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                updates: { [item.id]: 0 }
            })
        });

        if (!removeResponse.ok) {
            throw new Error(`Failed to remove original product: ${removeResponse.statusText}`);
        }

    }

    /**
     * Updates the button states
     * @param {boolean} isLoading - Whether the buttons should be disabled and loading text should be shown
     * @returns {void}
     */
    updateButtonStates(isLoading) {
        const buttons = document.querySelectorAll('[data-prescription-action]');
        if (!buttons.length) return;
        buttons.forEach(button => {
            if (!(button instanceof HTMLButtonElement)) return;
            button.disabled = isLoading;
            if (isLoading) {
                button.dataset.originalText = button.textContent || '';
                button.textContent = 'Verarbeitung...';
            } else {
                button.textContent = button.dataset.originalText || button.textContent;
            }
        });
    }

    /**
     * Updates button behavior based on cart state
     * @returns {void}
     */
    updateButtonBehavior() {
        const cartItems = document.querySelectorAll('cart-items-component tbody .cart-items__table-row:not(.cart-items__table-row--placeholder)');
        const hasItems = cartItems.length > 0;

        const buttons = document.querySelectorAll('[data-prescription-action]');
        buttons.forEach(button => {
            if (!(button instanceof HTMLButtonElement)) return;

            if (hasItems) {
                button.removeAttribute('onclick');
                button.setAttribute('data-prescription-action', button.dataset.prescriptionAction || '');
                button.style.cursor = 'pointer';
            } else {
                const action = button.dataset.prescriptionAction;
                const url = action === 'product-with-recipe'
                    ? button.dataset.physicalCollectionUrl
                    : button.dataset.digitalCollectionUrl;
                if (url) {
                    button.removeAttribute('data-prescription-action');
                    button.setAttribute('onclick', `window.location.href='${url}'`);
                    button.style.cursor = 'pointer';
                }
            }
        });
    }

    /**
     * Shows an error message
     * @param {string} message - The message to show
     * @returns {void}
     */
    showError(message) {
        const errorMessage = document.createElement('div');
        errorMessage.classList.add('prescription-error');
        errorMessage.textContent = message;
        document.body.appendChild(errorMessage);
    }

    init() {
        document.addEventListener('DOMContentLoaded', () => {
            this.updateButtonBehavior();
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'childList') {
                        this.updateButtonBehavior();
                    }
                });
            });
            const cartContainer = document.querySelector('#cart-form tbody');
            if (cartContainer) {
                observer.observe(cartContainer, { childList: true, subtree: true });
            }

            document.addEventListener('click', (e) => {
                if (!(e.target instanceof HTMLElement)) return;
                const button = e.target.closest('[data-prescription-action]');
                if (!button) return;

                if (button.matches('[data-prescription-action="recipe-only"]')) {
                    e.preventDefault();
                    this.processRecipeOnly();
                } else if (button.matches('[data-prescription-action="product-with-recipe"]')) {
                    e.preventDefault();
                    this.processProductWithRecipe();
                }
            })
        })
    }
}

const cart_delivery_type = 'physical';
const prescriptionSwap = new PrescriptionSwap();
prescriptionSwap.init();
